// frontend/src/pages/ProviderServiceManagement.tsx
// Mobile-first design without modals

import React, { useState } from 'react';
import { Plus, MessageSquare, Filter, Search } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { toast } from '../utils/toast';
import {
  useProviderServices,
  useDeleteProviderService,
  useToggleServiceStatus,
  useToggleServiceFeatured,
  useServiceFilterOptions,
  useServiceStats,
} from '@/hooks/useProviderServicesQueries';
import {
  ProviderService,
  ProviderServiceFilters
} from '@/lib/api/providerServicesService';

// Components
import ServiceStatsCard from '@/components/ProviderServices/ServiceStatsCard';
import ServiceCard from '@/components/ProviderServices/ServiceCard';
import EmptyServicesState from '@/components/ProviderServices/EmptyServicesState';
import ServiceFilters from '@/components/ProviderServices/ServiceFilters';
import { LoadingState, ErrorState, Pagination, PageHeader, PageHeaderActions, FilterButton } from '@/components/common/ui';

const ProviderServiceManagement: React.FC = () => {
  const navigate = useNavigate();
  
  // Filter state
  const [filters, setFilters] = useState<ProviderServiceFilters>({
    search: '',
    category_id: undefined,
    service_type_id: undefined,
    is_active: undefined,
    is_featured: undefined,
    page: 1,
    limit: 20,
  });

  // UI state
  const [showFilters, setShowFilters] = useState(false);

  // Data queries
  const { data: servicesData, isLoading: isLoadingServices, error } = useProviderServices(filters);
  const { data: filterOptions } = useServiceFilterOptions();
  const { data: statsData } = useServiceStats();

  // Extract data from responses
  const services = servicesData?.services || [];
  const pagination = servicesData?.pagination;
  const stats = servicesData?.stats || statsData;

  // Mutations
  const deleteServiceMutation = useDeleteProviderService();
  const toggleStatusMutation = useToggleServiceStatus();
  const toggleFeaturedMutation = useToggleServiceFeatured();

  // Computed values
  const hasServices = services.length > 0;
  const isLoading = isLoadingServices;
  const hasActiveFilters = !!(filters.search || filters.category_id || filters.service_type_id || filters.is_active !== undefined || filters.is_featured !== undefined);

  // Event Handlers
  const handleFilterChange = (newFilters: Partial<ProviderServiceFilters>) => {
    setFilters(prev => ({
      ...prev,
      ...newFilters,
      page: 1, // Reset to first page when filters change
    }));
  };

  const handlePageChange = (page: number) => {
    setFilters(prev => ({ ...prev, page }));
  };

  const handleEdit = (service: ProviderService) => {
    navigate(`/provider-services/${service.provider_service_id}/edit`);
  };

  const handleDelete = async (service: ProviderService) => {
    const confirmMessage = `Are you sure you want to delete "${service.service_name}"? This action cannot be undone.`;
    
    if (window.confirm(confirmMessage)) {
      try {
        await deleteServiceMutation.mutateAsync(service.provider_service_id);
      } catch (error: any) {
        toast.error(error.message || 'Failed to delete service');
      }
    }
  };

  const handleToggleStatus = async (serviceId: number) => {
    try {
      await toggleStatusMutation.mutateAsync(serviceId);
    } catch (error: any) {
      toast.error(error.message || 'Failed to toggle service status');
    }
  };

  const handleToggleFeatured = async (serviceId: number) => {
    try {
      await toggleFeaturedMutation.mutateAsync(serviceId);
    } catch (error: any) {
      toast.error(error.message || 'Failed to toggle featured status');
    }
  };

  // Bulk operations - REMOVED
  // All bulk operation handlers have been removed


  const handleClearFilters = () => {
    setFilters({
      search: '',
      category_id: undefined,
      service_type_id: undefined,
      is_active: undefined,
      is_featured: undefined,
      page: 1,
      limit: 20,
    });
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="mx-auto max-w-7xl px-4 py-6 sm:px-6 lg:px-8">
        {/* Simple Page Title - Just text, no card */}
        <div className="mb-6">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Services</h1>
              <p className="mt-1 text-sm text-gray-500">
                Manage your services and offerings
              </p>
            </div>
            <div className="flex items-center gap-3">
              <FilterButton
                onClick={() => setShowFilters(!showFilters)}
                hasActiveFilters={hasActiveFilters}
                filterCount={Object.values(filters).filter(v => v !== undefined && v !== '' && v !== 1).length}
              />
            </div>
          </div>
        </div>

        {/* Stats as Info Section - No card wrapper */}
        {stats && (
          <div className="mb-6">
            <h2 className="text-lg font-medium text-gray-900 mb-4">Overview</h2>
            <ServiceStatsCard stats={stats} />
          </div>
        )}

        {/* Filters Section - Outside card */}
        {showFilters && (
          <div className="mb-6">
            <ServiceFilters
              filters={filters}
              filterOptions={filterOptions}
              onFilterChange={handleFilterChange}
              onClearFilters={handleClearFilters}
              hasActiveFilters={hasActiveFilters}
            />
          </div>
        )}

        {/* Search Bar - Mobile First */}
        <div className="mb-6">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={18} />
            <input
              type="text"
              placeholder="Search services..."
              value={filters.search || ''}
              onChange={(e) => handleFilterChange({ search: e.target.value })}
              className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>
        </div>

        {/* Actions - Below filters, above listing */}
        <div className="mb-6">
          <PageHeaderActions
            primaryAction={{
              label: "Add Service",
              icon: Plus,
              onClick: () => navigate('/provider-services/browse'),
            }}
            secondaryActions={[
              {
                label: "Request",
                icon: MessageSquare,
                onClick: () => navigate('/provider-services/request'),
                variant: "outline",
              },
            ]}
          />
        </div>

        {/* Mobile: No card wrapper for max space */}
        <div className="block xl:hidden">
          {/* Mobile Header */}
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-medium text-gray-900">Services</h3>
            {services && (
              <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                {services.length} services
              </span>
            )}
          </div>
          {error ? (
            <div className="bg-white rounded-lg border border-gray-200 shadow-sm p-6">
              <ErrorState
                type="server"
                title="Error loading services"
                message={error.message || 'Failed to load services'}
                onRetry={() => window.location.reload()}
                size="md"
              />
            </div>
          ) : isLoading ? (
            <div className="bg-white rounded-lg border border-gray-200 shadow-sm p-6">
              <LoadingState
                variant="skeleton"
                title="Loading services..."
                message="Please wait while we fetch your services"
                size="md"
              />
            </div>
          ) : hasServices ? (
            <>
              {/* Mobile Service List */}
              <div className="space-y-4">
                {services.map((service) => (
                  <ServiceCard
                    key={service.provider_service_id}
                    service={service}
                    onEdit={handleEdit}
                    onDelete={handleDelete}
                    onToggleStatus={handleToggleStatus}
                    onToggleFeatured={handleToggleFeatured}
                  />
                ))}
              </div>
              
              {/* Mobile Pagination */}
              {pagination && (
                <div className="mt-6 bg-white rounded-lg border border-gray-200 shadow-sm p-4">
                  <Pagination
                    currentPage={pagination.current_page || 1}
                    totalPages={pagination.total_pages || 1}
                    totalItems={pagination.total || 0}
                    itemsPerPage={pagination.per_page || 10}
                    onPageChange={handlePageChange}
                    variant="simple"
                    size="sm"
                  />
                </div>
              )}
            </>
          ) : (
            <div className="bg-white rounded-lg border border-gray-200 shadow-sm p-6">
              <EmptyServicesState
                onBrowseServices={() => navigate('/provider-services/browse')}
                onRequestService={() => navigate('/provider-services/request')}
                hasFilters={hasActiveFilters}
                onClearFilters={handleClearFilters}
              />
            </div>
          )}
        </div>

        {/* Desktop: Card wrapper for clean look */}
        <div className="hidden xl:block bg-white rounded-lg border border-gray-200 shadow-sm">
          {/* Card Header with count */}
          <div className="px-6 py-4 border-b border-gray-200">
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-medium text-gray-900">Services</h3>
              {services && (
                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                  {services.length} services
                </span>
              )}
            </div>
          </div>

          {/* Desktop Content */}
          <div className="p-6">
            {error ? (
              <ErrorState
                type="server"
                title="Error loading services"
                message={error.message || 'Failed to load services'}
                onRetry={() => window.location.reload()}
                size="md"
              />
            ) : isLoading ? (
              <LoadingState
                variant="skeleton"
                title="Loading services..."
                message="Please wait while we fetch your services"
                size="md"
              />
            ) : hasServices ? (
              <>
                {/* Desktop Service List */}
                <div className="divide-y divide-gray-200">
                  {services.map((service) => (
                    <ServiceCard
                      key={service.provider_service_id}
                      service={service}
                      onEdit={handleEdit}
                      onDelete={handleDelete}
                      onToggleStatus={handleToggleStatus}
                      onToggleFeatured={handleToggleFeatured}
                    />
                  ))}
                </div>
                
                {/* Desktop Pagination */}
                {pagination && (
                  <div className="mt-6 -mx-6 -mb-6 px-6 py-4 border-t border-gray-200 bg-gray-50">
                    <Pagination
                      currentPage={pagination.current_page || 1}
                      totalPages={pagination.total_pages || 1}
                      totalItems={pagination.total || 0}
                      itemsPerPage={pagination.per_page || 10}
                      onPageChange={handlePageChange}
                      variant="default"
                      size="md"
                    />
                  </div>
                )}
              </>
            ) : (
              <EmptyServicesState
                onBrowseServices={() => navigate('/provider-services/browse')}
                onRequestService={() => navigate('/provider-services/request')}
                hasFilters={hasActiveFilters}
                onClearFilters={handleClearFilters}
              />
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProviderServiceManagement;